import {RolesType} from "./roles.type"

export type PermissionSection =
  'thesis' | 'board' | 'crm' | 'engines' | 'scheduler' | 'linkedin' | 'assistant' | 'integrations' | 'settings' | 'team' | 'billing'

export type PermissionLevel = 'none' | 'read' | 'write'

/** An admin bypasses every section check; a member is governed by their permissions map. */
export type MemberRole = 'admin' | 'member'

/** What is stored on the workspace doc: workspaces/{id}.permissions[uid]. Sparse on purpose. */
export type MemberPermissions = Partial<Record<PermissionSection, PermissionLevel>>

/** Every section resolved to a level for one user — what the app reads. */
export type ResolvedPermissions = Record<PermissionSection, PermissionLevel>

/** The sections an admin actually ticks. Integrations is deliberately absent: it is derived. */
export const PERMISSION_SECTIONS: { key: PermissionSection, name: string, short: string }[] = [
  { key: 'thesis', name: 'Commercial Thesis', short: 'Thesis' },
  { key: 'board', name: 'Execution Board', short: 'Board' },
  { key: 'crm', name: 'CRM', short: 'CRM' },
  { key: 'engines', name: 'Engines', short: 'Engines' },
  { key: 'scheduler', name: 'Scheduler', short: 'Scheduler' },
  { key: 'linkedin', name: 'LinkedIn Campaigns', short: 'LinkedIn' },
  { key: 'assistant', name: 'AI Assistant', short: 'Assistant' },
  { key: 'settings', name: 'Workspace Settings', short: 'Settings' },
  { key: 'team', name: 'Team Members', short: 'Team' },
  { key: 'billing', name: 'Billing', short: 'Billing' }
]

export const ALL_SECTIONS: { key: PermissionSection, name: string, short: string }[] = [
  ...PERMISSION_SECTIONS,
  { key: 'integrations', name: 'Integrations', short: 'Integrations' }
]

/** Sections every member can always see — ticking them only ever grants write. */
export const ALWAYS_READABLE: PermissionSection[] = ['team', 'settings']

/** Sections with no legacy fallback: not granted explicitly means not granted. Everything else
 *  falls back to the editors/users arrays so workspaces predating the map keep working, but that
 *  fallback would hand LinkedIn campaigns to every member of a workspace the moment it is linked —
 *  and each member who opens it becomes a user in the campaigns app. Opt in, per person.
 *
 *  `assistant` is here for the same shape of reason: every turn spends money on a model, so the
 *  fallback would quietly put every existing member of every workspace on a metered feature.
 *  Admins bypass this (see `storedLevel`), which is what makes it safe to launch opt-in. */
export const EXPLICIT_ONLY: PermissionSection[] = ['linkedin', 'assistant']

export const PERMISSION_LEVELS: { name: string, value: PermissionLevel }[] = [
  { name: 'No access', value: 'none' },
  { name: 'Read only', value: 'read' },
  { name: 'Read & write', value: 'write' }
]

/** A workspace the admin app has switched the feature on for. Both gated sections are off by
 *  default and turned on per workspace from the admin app. */
export type WorkspaceFeatures = { linkedinWorkspaceId?: string, assistantEnabled?: boolean }

/** Whether a gated section exists on this workspace at all. The assistant is metered, so it is
 *  sold before it is granted — same shape as LinkedIn, which appears once the workspace is linked. */
export function hasSection(workspace: WorkspaceFeatures | undefined, section: PermissionSection): boolean {
  if (section === 'linkedin') { return !!workspace?.linkedinWorkspaceId }
  if (section === 'assistant') { return !!workspace?.assistantEnabled }
  return true
}

/** Sections that only exist on workspaces set up for them, so nobody ticks a box for a feature
 *  their workspace does not have. */
export function sectionsFor(workspace: WorkspaceFeatures | undefined) {
  return PERMISSION_SECTIONS.filter(section => hasSection(workspace, section.key))
}

type PermissionSource = {
  users: string[]
  admins: string[]
  editors: string[]
  permissions?: { [uid: string]: MemberPermissions }
}

/** What the workspace doc says, before the derived and clamped sections are applied. */
function storedLevel(workspace: PermissionSource, uid: string, section: PermissionSection): PermissionLevel {
  if (workspace.admins.includes(uid)) { return 'write' }
  if (EXPLICIT_ONLY.includes(section)) { return workspace.permissions?.[uid]?.[section] ?? 'none' }
  // Backwards compatibility for members with no stored map, mirroring scripts/migrate-permissions.js:
  // a legacy editor got everything except billing, a legacy reader got read on everything.
  const fallback: PermissionLevel = workspace.editors.includes(uid)
    ? (section === 'billing' ? 'none' : 'write')
    : 'read'
  return workspace.permissions?.[uid]?.[section] ?? fallback
}

function bestOf(a: PermissionLevel, b: PermissionLevel): PermissionLevel {
  if (a === 'write' || b === 'write') { return 'write' }
  return (a !== 'none' || b !== 'none') ? 'read' : 'none'
}

/**
 * Mirrors memberLevel() in firestore.rules — keep the two in sync.
 * Sparse by design: a uid or a section missing from the map falls back to the legacy
 * admins/editors arrays, so existing workspaces keep working with no backfill.
 */
export function permissionLevel(workspace: PermissionSource | undefined, uid: string, section: PermissionSection): PermissionLevel {
  // Non-members get nothing — without this the editors fallback would hand a stranger read
  // access to every section. Mirrors isMember() in firestore.rules.
  if (!workspace || !uid || !workspace.users.includes(uid)) { return 'none' }
  // Integrations rides on the sections that need to connect one — booking a meeting needs a
  // calendar, running an engine needs its destinations — so it is the best of those two.
  if (section === 'integrations') {
    return bestOf(storedLevel(workspace, uid, 'engines'), storedLevel(workspace, uid, 'scheduler'))
  }
  const level = storedLevel(workspace, uid, section)
  return (ALWAYS_READABLE.includes(section) && level === 'none') ? 'read' : level
}

export function resolvePermissions(workspace: PermissionSource | undefined, uid: string): ResolvedPermissions {
  return ALL_SECTIONS.reduce((acc, section) => {
    acc[section.key] = permissionLevel(workspace, uid, section.key)
    return acc
  }, {} as ResolvedPermissions)
}

/**
 * Bridges a resolved section level back to the legacy 'admin' | 'editor' | 'viewer' shape that
 * older templates still gate on. Admins keep their role, write becomes 'editor', anything else
 * reads as 'viewer'. New templates should use the `can` pipe instead.
 */
export function sectionRole(role: RolesType, level: PermissionLevel | undefined): RolesType {
  if (!role || role === 'admin') { return role }
  return level === 'write' ? 'editor' : 'viewer'
}
